import { submitChatMessages } from "./submitChatMessages";
import { updateChatFormInputHeight } from "./updateChatFormInputHeight";
import { GenerateContent } from "./generateContent";
import { appendAdminMessage } from "./appendAdminMessage";
import { scrollToChatBottom } from "./scrollToChatBottom";
import { appendUserMessage } from "./appendUserMessage";
import { cancelLoadMessagesOneByOne } from "./loadMessagesOneByOne";
import { isTypingActive } from "./typingAnimation";
import {
  getLoaderDelayMs,
  getSubmitDebounceDelay,
} from "../helpers/frontendDelays";
import {
  appendGuestLocalHistory,
  buildGuestLocalRenderedMessageEntry,
  shouldUseGuestLocalHistoryMode,
} from "../helpers/guestLocalHistory";
import { chatDebugLog } from "../helpers/chatDebug";

const $ = jQuery;

function getInlineQuizAnswerSource(chatForm) {
  return chatForm.data("inlineQuizAnswerSource") || null;
}

function setInlineQuizAnswerSource(chatForm, messageElement) {
  if (!messageElement || !messageElement.length) {
    console.error("[growtype-chat][inline-quiz-answer] Missing source message.");
    return;
  }

  chatDebugLog('inlineQuiz',"[growtype-chat][inline-quiz-answer] source-set", {
    sourceRole: messageElement.attr("data-role") || "",
    sourceMessageType: messageElement.attr("data-message-type") || "",
  });
  chatForm.data("inlineQuizAnswerSource", messageElement);
}

function clearInlineQuizAnswerSource(chatForm) {
  chatDebugLog('inlineQuiz',"[growtype-chat][inline-quiz-answer] source-cleared");
  chatForm.removeData("inlineQuizAnswerSource");
}

function markInlineQuizFastSubmit(chatForm, isActive = true) {
  if (!chatForm || !chatForm.length) {
    return;
  }

  if (isActive) {
    chatForm.data("inlineQuizFastSubmit", true);
  } else {
    chatForm.removeData("inlineQuizFastSubmit");
  }
}

function consumeInlineQuizFastSubmit(chatForm) {
  const isActive = chatForm.data("inlineQuizFastSubmit") === true;
  markInlineQuizFastSubmit(chatForm, false);
  return isActive;
}

function consumeInlineQuizAnswerSource(chatForm) {
  const sourceMessage = getInlineQuizAnswerSource(chatForm);

  clearInlineQuizAnswerSource(chatForm);

  if (!sourceMessage || !sourceMessage.length) {
    chatDebugLog('inlineQuiz',"[growtype-chat][inline-quiz-answer] nothing-to-consume");
    return;
  }

  const quizWrapper = sourceMessage.find(".growtype-quiz-wrapper").first();
  const answersWrapper = sourceMessage.find(
    ".growtype-quiz-question-answers-wrapper"
  );
  const answerButtons = sourceMessage.find(
    '.growtype-quiz-question-answer[data-chat-inline-answer="true"]'
  );

  chatDebugLog('inlineQuiz',"[growtype-chat][inline-quiz-answer] preserving-source-message", {
    hasQuizWrapper: quizWrapper.length > 0,
    hasAnswersWrapper: answersWrapper.length > 0,
    answerButtons: answerButtons.length,
  });

  sourceMessage.attr("data-inline-quiz-consumed", "true");
  sourceMessage.attr("data-inline-quiz-answered", "true");

  if (quizWrapper.length) {
    answerButtons.each(function () {
      const button = $(this);

      button.prop("disabled", true);
      button.attr("aria-disabled", "true");
      button.removeAttr("data-chat-inline-answer");
      button.addClass("is-disabled");
    });

    quizWrapper.remove();

    if (
      shouldUseGuestLocalHistoryMode() &&
      sourceMessage.attr("data-inline-quiz-history-saved") !== "true"
    ) {
      const storedEntry = buildGuestLocalRenderedMessageEntry(sourceMessage);

      if (storedEntry) {
        appendGuestLocalHistory(chatForm, [storedEntry]);
        sourceMessage.attr("data-inline-quiz-history-saved", "true");
      }
    }

    return;
  }

  answerButtons.each(function () {
    const button = $(this);

    button.prop("disabled", true);
    button.attr("aria-disabled", "true");
    button.removeAttr("data-chat-inline-answer");
    button.addClass("is-disabled");
  });

  if (
    shouldUseGuestLocalHistoryMode() &&
    sourceMessage.attr("data-inline-quiz-history-saved") !== "true"
  ) {
    const storedEntry = buildGuestLocalRenderedMessageEntry(sourceMessage);

    if (storedEntry) {
      appendGuestLocalHistory(chatForm, [storedEntry]);
      sourceMessage.attr("data-inline-quiz-history-saved", "true");
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────

/** Only show the pending assistant bubble when nothing else is animating. */
function canShowPendingAssistantBubble(chatForm) {
  const hasTypingAnimation =
    chatForm.find(".typing-animation-container").length > 0;
  const hasActiveIncomingLoader =
    chatForm.find(
      ".g-chatform-message.is-new-message:not(.is-pending-response) .g-chatform-message-content-loader:visible, .g-chatform-message.is-new-message:not(.is-pending-response) .g-chatform-content-generating-loader:not(.d-none)"
    ).length > 0;

  return !hasTypingAnimation && !hasActiveIncomingLoader && !isTypingActive();
}

function getCategory(input) {
  return input.attr("data-category") ?? "text";
}

function getNsfw(chatForm) {
  return (
    chatForm.find(".g-chatform-text-input").attr("data-nsfw") ??
    chatForm.find('input[name="nsfw_chat"]').is(":checked") ??
    false
  );
}

function isMediaCategory(category) {
  return ["image", "video", "audio"].includes(category);
}

function showLoaderAfterDelay(template, chatForm, delayMs) {
  setTimeout(() => {
    if (canShowPendingAssistantBubble(chatForm)) {
      template.show();
      scrollToChatBottom(300, true);
    }
  }, delayMs);
}

function buildPendingData(template, category, nsfw) {
  return {
    value: "",
    skip_question_preview: true,
    userMessageTemplate: template,
    adminMessageTemplate: null,
    category,
    nsfw,
    frontend_delay_case: "default",
    inline_source_message_id: "",
  };
}

function closeGeneratorAndReset(chatFormInput) {
  const generateContent = new GenerateContent();
  generateContent.resetInput();
  generateContent.close();
  updateChatFormInputHeight();
}

function handleTriggeredChatMessage(event) {
  const message = event.detail?.message;
  const skipSaving = event.detail?.skipSaving || false;
  const skipQuestionPreview = event.detail?.skipQuestionPreview || false;
  const adultModeRetry = event.detail?.adultModeRetry || false;
  const adultModeSafeContinuation =
    event.detail?.adultModeSafeContinuation || false;

  if (!message) return;

  const chatForm = $(".g-chatform").first();
  const submitBtn = chatForm.find(
    ".g-chatform-footer-submit-wrapper .btn-send"
  );

  if (!submitBtn.hasClass("is-disabled")) {
    submitChatMessages(
      {
        0: {
          value: message,
          skip_saving: skipSaving,
          skip_question_preview: skipQuestionPreview,
          adult_mode_retry: adultModeRetry,
          adult_mode_safe_continuation: adultModeSafeContinuation,
          // An automatic retry reuses the already-rendered user turn. Passing
          // an empty jQuery collection prevents submitChatMessages() from
          // appending a duplicate human bubble while still rendering the new
          // assistant loader and response.
          adminMessageTemplate: skipQuestionPreview ? jQuery() : null,
          interaction_type: "composer",
        },
      },
      chatForm
    );
  }
}

// ── Submit logic ─────────────────────────────────────────────────

export function submitMessage() {
  let chatFormInput = jQuery(
    ".g-chatform-footer-input-wrapper .g-chatform-text-input"
  );

  $(document)
    .off("click.growtypeChatInlineQuizAnswer")
    .on(
      "click.growtypeChatInlineQuizAnswer",
      '.g-chatform .growtype-quiz-question-answer[data-chat-inline-answer="true"]',
      function (event) {
        event.preventDefault();
        event.stopPropagation();

        const answer = $(this);
        const chatForm = answer.closest(".g-chatform");
        const chatFormInput = chatForm.find(".g-chatform-text-input").first();
        const submitBtn = chatForm
          .find(".g-chatform-footer-submit-wrapper .btn-send")
          .first();
        const answerValue =
          answer.attr("data-chat-inline-answer-value") ||
          answer.attr("data-value") ||
          answer.text().trim();
        const sourceMessage = answer.closest(".g-chatform-message");

        chatDebugLog('inlineQuiz',"[growtype-chat][inline-quiz-answer] click", {
          answerValue,
          hasSourceMessage: sourceMessage.length > 0,
          sourceMessageRole: sourceMessage.attr("data-role") || "",
        });

        if (!chatForm.length || !chatFormInput.length || !submitBtn.length) {
          console.error(
            "[growtype-chat][inline-quiz-answer] Missing chat submit elements.",
            {
              hasChatForm: chatForm.length > 0,
              hasInput: chatFormInput.length > 0,
              hasSubmitBtn: submitBtn.length > 0,
            }
          );
          return;
        }

        setInlineQuizAnswerSource(chatForm, sourceMessage);
        markInlineQuizFastSubmit(chatForm, true);
        chatFormInput.val(answerValue).trigger("input").focus();
        submitBtn.trigger("click");
      }
    );

  // Enter key triggers send
  chatFormInput
    .off("keypress.growtypeChatSubmitMessage")
    .on("keypress.growtypeChatSubmitMessage", function (evt) {
      if (evt.keyCode == 13 && !evt.shiftKey) {
        $(this)
          .closest(".g-chatform")
          .find(".g-chatform-footer-submit-wrapper .btn-send")
          .first()
          .trigger("click");
        return false;
      }
    });

  // Input height
  chatFormInput
    .off(
      "input.growtypeChatSubmitMessage focusin.growtypeChatSubmitMessage focusout.growtypeChatSubmitMessage"
    )
    .on(
      "input.growtypeChatSubmitMessage focusin.growtypeChatSubmitMessage",
      () => updateChatFormInputHeight()
    )
    .on("focusout.growtypeChatSubmitMessage", () =>
      updateChatFormInputHeight({ reset: true })
    );

  // Track interaction with generate panel (keep "is-typing" while inside)
  let isInteractingWithGeneratePanel = false;

  chatFormInput
    .off("focus.growtypeChatSubmitTyping focusout.growtypeChatSubmitTyping")
    .on("focus.growtypeChatSubmitTyping", function () {
      $(this).closest(".g-chatform").addClass("is-typing");
    })
    .on("focusout.growtypeChatSubmitTyping", function (e) {
      const generatePanel = $(".content-generator-settings-inner")[0];
      if (
        generatePanel &&
        (generatePanel.contains(e.relatedTarget) ||
          isInteractingWithGeneratePanel)
      ) {
        return;
      }
      $(this).closest(".g-chatform").removeClass("is-typing");
    });

  $(document)
    .off(
      "mousedown.growtypeChatSubmitGenerate mouseup.growtypeChatSubmitGenerate click.growtypeChatSubmitGenerate",
      ".content-generator-settings-inner"
    )
    .on(
      "mousedown.growtypeChatSubmitGenerate mouseup.growtypeChatSubmitGenerate click.growtypeChatSubmitGenerate",
      ".content-generator-settings-inner",
      function (e) {
        if (e.type === "mousedown") {
          isInteractingWithGeneratePanel = true;
        } else if (e.type === "mouseup" || e.type === "click") {
          setTimeout(() => {
            isInteractingWithGeneratePanel = false;
          }, 100);
        }
      }
    );

  $(document)
    .off(
      "touchstart.growtypeChatSubmitGenerate touchend.growtypeChatSubmitGenerate",
      ".content-generator-settings-inner"
    )
    .on(
      "touchstart.growtypeChatSubmitGenerate touchend.growtypeChatSubmitGenerate",
      ".content-generator-settings-inner",
      function (e) {
        if (e.type === "touchstart") {
          isInteractingWithGeneratePanel = true;
        } else if (e.type === "touchend") {
          setTimeout(() => {
            isInteractingWithGeneratePanel = false;
          }, 100);
        }
      }
    );

  // ── Debounce ─────────────────────────────────────────────────

  let submitDebounceTimer = null;
  let pendingMessageData = null;
  let pendingChatForm = null;

  function schedulePendingSubmit(immediate = false) {
    if (submitDebounceTimer) clearTimeout(submitDebounceTimer);

    const runSubmit = () => {
      if (pendingMessageData && pendingChatForm) {
        const domTemplate = pendingMessageData.userMessageTemplate;
        const safePayload = {
          value: pendingMessageData.value,
          skip_question_preview: pendingMessageData.skip_question_preview,
          userMessageTemplate: domTemplate,
          adminMessageTemplate: pendingMessageData.adminMessageTemplate,
          category: pendingMessageData.category,
          nsfw: pendingMessageData.nsfw,
          frontend_delay_case: pendingMessageData.frontend_delay_case,
          inline_source_message_id: pendingMessageData.inline_source_message_id,
          interaction_type:
            pendingMessageData.frontend_delay_case === "inline_quiz_answer"
              ? "inline_quiz_answer"
              : "composer",
        };
        submitChatMessages({ 0: safePayload }, pendingChatForm);
      }
      submitDebounceTimer = null;
      pendingMessageData = null;
      pendingChatForm = null;
    };

    if (immediate) {
      runSubmit();
    } else {
      const delayMs = getSubmitDebounceDelay(
        pendingChatForm,
        pendingMessageData?.frontend_delay_case || "default"
      );
      submitDebounceTimer = setTimeout(runSubmit, delayMs);
    }
  }

  // ── Send button ───────────────────────────────────────────────

  jQuery(".g-chatform .g-chatform-footer-submit-wrapper .btn-send")
    .off("click.growtypeChatSubmitMessage")
    .on("click.growtypeChatSubmitMessage", function () {
      let submitBtn = $(this);

      if (submitBtn.hasClass("is-disabled")) return;

      let chatForm = submitBtn.closest(".g-chatform");
      const chatFormInput = chatForm.find(".g-chatform-text-input").first();
      let messageValue = String(chatFormInput.val() || "");
      const isInlineQuizFastSubmit = consumeInlineQuizFastSubmit(chatForm);
      const inlineQuizSourceMessage = getInlineQuizAnswerSource(chatForm);
      const inlineSourceMessageId = inlineQuizSourceMessage?.attr("data-id") || "";

      console.log("[growtype-chat][submit] click", {
        messageValue,
        hasInlineQuizSource: !!getInlineQuizAnswerSource(chatForm),
        isInlineQuizFastSubmit,
        inlineSourceMessageId,
      });

      if (messageValue.length === 0) {
        const inputWrapper = chatForm.find(".g-chatform-footer-input-wrapper");
        inputWrapper.addClass("anim-shake");
        setTimeout(() => {
          inputWrapper.removeClass("anim-shake");
        }, 500);
        return;
      }

      cancelLoadMessagesOneByOne();

      consumeInlineQuizAnswerSource(chatForm);

      // Show the user's message immediately
      const adminMessageTemplate = appendAdminMessage(
        chatForm,
        messageValue,
        false
      );

      if (pendingMessageData && pendingMessageData.userMessageTemplate) {
        // Reuse existing pending template — move it to the bottom
        pendingMessageData.userMessageTemplate.detach();
        jQuery(".g-chatform-content").append(
          pendingMessageData.userMessageTemplate
        );
        if (isInlineQuizFastSubmit) {
          pendingMessageData.userMessageTemplate.show();
        } else {
          showLoaderAfterDelay(
            pendingMessageData.userMessageTemplate,
            chatForm,
            getLoaderDelayMs(
              chatForm,
              pendingMessageData.frontend_delay_case || "default",
              pendingMessageData.category ?? "text"
            )
          );
        }
      } else {
        const category = getCategory(chatFormInput);
        const nsfw = getNsfw(chatForm);
        const template = appendUserMessage(chatForm, category);

        if (isInlineQuizFastSubmit) {
          template.show();
        } else {
          showLoaderAfterDelay(
            template,
            chatForm,
            getLoaderDelayMs(chatForm, "default", category)
          );
        }
        pendingMessageData = buildPendingData(template, category, nsfw);
      }

      scrollToChatBottom(500, true);

      // Append message value (concatenate if debouncing)
      if (pendingMessageData.value) {
        pendingMessageData.value += "\n" + messageValue;
      } else {
        pendingMessageData.value = messageValue;
      }

      // Refresh metadata in case it changed
      pendingMessageData.category = getCategory(chatFormInput);
      pendingMessageData.nsfw = getNsfw(chatForm);
      pendingMessageData.adminMessageTemplate = adminMessageTemplate;
      pendingMessageData.frontend_delay_case = isInlineQuizFastSubmit
        ? "inline_quiz_answer"
        : "default";
      pendingMessageData.inline_source_message_id = isInlineQuizFastSubmit
        ? inlineSourceMessageId
        : "";

      pendingChatForm = chatForm;

      // Clear input
      chatFormInput.val("");
      closeGeneratorAndReset(chatFormInput);

      const isText = (pendingMessageData.category ?? "text") === "text";
      schedulePendingSubmit(isInlineQuizFastSubmit || !isText);
    });

  // ── Programmatic trigger (bypasses debounce) ──────────────────

  document.removeEventListener(
    "growtypeChatTriggerMessage",
    handleTriggeredChatMessage
  );
  document.addEventListener(
    "growtypeChatTriggerMessage",
    handleTriggeredChatMessage
  );
}
